import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';

import MenuContext from 'contexts/menuContext';

import { useWebSocketLazy } from 'helpers/wsConnection';

import { CardState, MaintenanceValidationHistItem, MaintenanceValidationStatus } from './types';
import {
  requestMaintenanceValidation, cancelMaintenanceValidation, getActiveMaintenanceValidation, getMaintenanceValidationHistory,
  MaintenanceValidationHistoryResult,
} from './helpers/api';
import { IdleState } from './parts/IdleState';
import { ProgressState } from './parts/ProgressState';
import { WaitingState } from './parts/WaitingState';
import { FailureState } from './parts/FailureState';
import { ConfirmModal } from './parts/ConfirmModal';
import { HistoryTable, PAGE_SIZE } from './parts/HistoryTable';

import { CardContainer } from './styles';

interface Props {
  devId: string;
  assetId: number | null | undefined;
  machineId: number | null | undefined;
  machineType?: string | null;
  timezoneArea?: string | null;
  latestHealthDatReport?: number;
  onMachineUnderAnalysis: (value: boolean) => void;
}

const SUPPORTED_DUT_MACHINE_TYPES = [
  'split-cassete',
  'split-duto',
  'split-hi-wall',
  'split-piso-teto',
  'tipo-fancoil',
];

export function shouldShowMaintenanceValidation(devId: string, machineType?: string | null): boolean {
  if (devId.startsWith('DAC')) return true;
  if (devId.startsWith('DUT')) return !!machineType && SUPPORTED_DUT_MACHINE_TYPES.includes(machineType);
  return false;
}

export function getValidationDurationMinutes(devId: string): number {
  return devId.startsWith('DAC') ? 35 : 30;
}

export function MaintenanceValidationCardLayout({
  devId, assetId, machineId, machineType, timezoneArea, latestHealthDatReport, onMachineUnderAnalysis,
}: Readonly<Props>): JSX.Element {
  const { t } = useTranslation();
  const { menuToogle } = useContext(MenuContext);
  const lws = useWebSocketLazy();
  const [showModal, setShowModal] = useState(false);

  const [state, setState] = useState({
    cardState: 'idle' as CardState,
    activeValidation: null as Partial<MaintenanceValidationHistItem> | null,
    historyVersion: 0,
    failureReason: null as string | null,
    ruleTitle: null as string | null,
    startTime: null as string | null,
    completedAt: null as string | null,
    isLoading: true,
    isStarting: false,
    isCanceling: false,
    initialHistory: null as MaintenanceValidationHistoryResult | null,
  });

  const durationMinutes = getValidationDurationMinutes(devId);

  function getDeadlineLabel(startTime: string) {
    return moment(startTime).add(durationMinutes, 'minutes').format('HH:mm');
  }

  async function handleConfirmStart() {
    setShowModal(false);
    if (!assetId || !machineId) {
      toast.error(t('erroIdentificarMaquinaValidacao'));
      return;
    }
    setState((s) => ({ ...s, isStarting: true }));
    const resp = await requestMaintenanceValidation({
      device_code: devId,
      asset_id: assetId,
      machine_id: machineId,
    });
    if (resp) {
      setState((s) => ({
        ...s,
        cardState: 'in_progress',
        startTime: new Date().toISOString(),
        activeValidation: { id: resp.validation_id },
      }));
    } else {
      toast.error(t('erroIniciarValidacao'));
    }
    setState((s) => ({ ...s, isStarting: false }));
  }

  async function handleCancel() {
    if (!state.activeValidation?.id) {
      setState((s) => ({
        ...s, cardState: 'idle', activeValidation: null, startTime: null, isCanceling: false,
      }));
      return;
    }
    setState((s) => ({ ...s, isCanceling: true }));
    if (!machineId) { toast.error(t('erroIdentificarMaquinaValidacao')); return; }
    const resp = await cancelMaintenanceValidation({ validation_id: state.activeValidation.id, device_code: devId, machine_id: machineId });
    if (resp) {
      setState((s) => ({
        ...s, cardState: 'idle', activeValidation: null, startTime: null,
      }));
    } else {
      toast.error(t('erroCancelarValidacao'));
    }
    setState((s) => ({ ...s, isCanceling: false }));
  }

  useEffect(() => {
    async function init() {
      setState((s) => ({ ...s, isLoading: true }));
      try {
        const [activeResp, histResp] = await Promise.all([
          machineId ? getActiveMaintenanceValidation({ device_code: devId, machine_id: machineId }) : Promise.resolve(undefined),
          machineId ? getMaintenanceValidationHistory({ device_code: devId, machine_id: machineId, limit: PAGE_SIZE }) : Promise.resolve(undefined),
        ]);
        const hist = histResp?.list ?? [];
        const active = activeResp?.active;
        if (histResp) {
          setState((s) => ({ ...s, initialHistory: histResp }));
        }
        if (active) {
          setState((s) => ({
            ...s,
            cardState: 'in_progress',
            activeValidation: active,
            startTime: active.start_time,
          }));
        } else {
          const lastItem = hist[0];
          const completedAt = lastItem?.completed_at;
          const within24h = completedAt && moment(completedAt).isAfter(moment().subtract(24, 'hours'));
          const healthIsNewer = completedAt && latestHealthDatReport && moment(latestHealthDatReport * 1000).isAfter(moment(completedAt));
          if (lastItem?.result?.status === 'positive' && within24h && !healthIsNewer) {
            setState((s) => ({ ...s, cardState: 'waiting', completedAt: completedAt ?? null }));
            onMachineUnderAnalysis(true);
          }
        }
        lws.start(onWsOpen, onWsMessage, beforeWsClose);
      } catch (err) {
        console.error(err);
      } finally {
        setState((s) => ({ ...s, isLoading: false }));
      }
    }
    init();
  }, []);

  function onWsOpen(wsConn) {
    wsConn.send(JSON.stringify({ type: 'subscribeMaintenanceValidation', data: { device_code: devId } }));
  }

  function refreshHistory() {
    setState((s) => ({ ...s, historyVersion: s.historyVersion + 1 }));
  }

  function onWsMessage(payload) {
    if (!payload || payload.type !== 'maintenanceValidationUpdate') return;
    const { data } = payload;
    if (data.device_code !== devId) return;
    if (data.event === 'start') {
      setState((s) => ({
        ...s, cardState: 'in_progress', startTime: data.start_time ?? null, activeValidation: { id: data.validation_id },
      }));
      onMachineUnderAnalysis(false);
    } else if (data.event === 'done') {
      const status: MaintenanceValidationStatus = data.payload?.status;
      const completedAt = data.payload?.completed_at ?? new Date().toISOString();
      if (status === 'positive') {
        setState((s) => ({ ...s, cardState: 'waiting', completedAt }));
        onMachineUnderAnalysis(true);
      } else if (status === 'negative') {
        setState((s) => ({
          ...s,
          cardState: 'failure',
          failureReason: data.payload?.reason ?? null,
          ruleTitle: data.payload?.rule_title ?? null,
          completedAt,
        }));
        onMachineUnderAnalysis(false);
      }
      refreshHistory();
    } else if (data.event === 'error') {
      setState((s) => ({ ...s, cardState: 'idle' }));
      onMachineUnderAnalysis(false);
      toast.error(t('erroValidacao'));
    } else if (data.event === 'cancel') {
      setState((s) => ({
        ...s, cardState: 'idle', activeValidation: null, startTime: null,
      }));
      onMachineUnderAnalysis(false);
    }
  }

  function beforeWsClose(wsConn) {
    wsConn.send(JSON.stringify({ type: 'unsubscribeMaintenanceValidation', data: { device_code: devId } }));
  }

  const deadlineLabel = state.startTime ? getDeadlineLabel(state.startTime) : '--:--';

  return (
    <>
      <CardContainer>
        {state.cardState === 'idle' && (
          <IdleState
            onStartClick={() => setShowModal(true)}
            isStarting={state.isStarting}
          />
        )}

        {state.cardState === 'in_progress' && (
          <ProgressState
            deadlineLabel={deadlineLabel}
            durationMinutes={durationMinutes}
            onCancel={handleCancel}
            isCanceling={state.isCanceling}
          />
        )}

        {state.cardState === 'waiting' && (
          <WaitingState
            completedAt={state.completedAt}
            timezoneArea={timezoneArea}
            onRestartClick={() => setShowModal(true)}
          />
        )}

        {state.cardState === 'failure' && (
          <FailureState
            failureReason={state.failureReason}
            ruleTitle={state.ruleTitle}
            completedAt={state.completedAt}
            timezoneArea={timezoneArea}
            onRestartClick={() => setShowModal(true)}
          />
        )}
      </CardContainer>

      <HistoryTable
        isLoading={state.isLoading}
        timezoneArea={timezoneArea}
        devId={devId}
        machineId={machineId}
        refreshToken={state.historyVersion}
        initialHistory={state.initialHistory}
      />

      {showModal && (
        <ConfirmModal
          onConfirm={handleConfirmStart}
          onClose={() => setShowModal(false)}
          isStarting={state.isStarting}
          sidebarWidth={menuToogle ? 260 : 70}
          durationMinutes={durationMinutes}
        />
      )}
    </>
  );
}
